// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button } from "react-bootstrap";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { ChargerConnectionType } from "shared/types/cloudtypes";
import { IAction } from "actions";
import { formatTimestamp, isSet, roundFix } from "shared/utils/utils";
import { IState } from "states/states";
import { IChargerStateEvent } from "states/trendingstates";
import {
  IConnectorConfig,
  IOcppCharger,
  IOcppChargingSession,
  IRfid,
  ITariff,
} from "states/ocppstates";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp/FormattedTime";
import ChargingSessionGraph from "view/comp/ChargingSessionGraph";
import ChargerData from "view/comp/ChargerData";
import OcppCommandButtons from "view/comp/OcppCommandButtons";
import ChargingPowerControl from "view/comp/ChargingPowerControl";

import IconPrint from "icons/iconPrint";

interface IChargingSessionProps {
  isRfidUser: boolean;
  isLoading: boolean;
  ocppCharger: IOcppCharger | undefined;
  connectorId: number;
  connector: IConnectorConfig | undefined;
  session: IOcppChargingSession | undefined;
  sessionEnergy: number | undefined;
  user: IRfid | undefined;
  tariff: ITariff | undefined;
  chargingStates: IChargerStateEvent[];
}

const mapStateToProps = (state: IState) => {
  // keep consistent with session loading in sagas:handleNavigation
  const session =
    state.ocpp.chargingSession.find((curItem) => curItem.id === state.router.transaction) ??
    (state.ocpp.pendingChargingSession[0] as IOcppChargingSession | undefined);
  const chargerId = session?.fk_charger;
  const chargerIndex = state.ocpp.charger.findIndex((curItem) => curItem.fk_charger === chargerId);
  const ocppCharger = state.ocpp.charger[chargerIndex];
  const props: IChargingSessionProps = {
    isRfidUser: !!state.site.user?.isRfidUser,
    isLoading:
      isSet(state.scheduler.chargingSession.lastRequest) &&
      state.scheduler.chargingSession.lastRequest > (state.scheduler.chargingSession.lastData ?? 0),
    ocppCharger,
    connectorId: session?.connector ?? 1,
    connector: state.ocpp.connectorConfig[chargerIndex]?.[session?.connector ?? 1],
    sessionEnergy:
      session ?
        state.ocpp.connectorData[chargerIndex]?.[session.connector]?.sessionEnergy
      : undefined,
    session,
    user: state.ocpp.rfid.find(
      (curItem) => curItem.rfid === session?.rfid || curItem.id === session?.rfidId,
    ),
    tariff: state.ocpp.siteTariff.find((curItem) => curItem.id === session?.fk_tariff),
    chargingStates: state.trending.chargerStateData ?? [],
  };
  return props;
};

interface IChargingSessionDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargingSessionDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  connector: {
    id: "Connector",
  },
  dataPower: {
    id: "charging power",
  },
  dataLimit: {
    id: "power limit",
  },
  printHint: {
    id: "print",
  },
});

class ChargingSessionComponent extends React.Component<
  IChargingSessionProps & IChargingSessionDispatch & RoutedProps
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  private isOngoingSession = false;

  private getStateEvents() {
    // show messages from station and connector; hide Etrel socket reconnect messages
    return (
      this.props.chargingStates
        ?.filter(
          (curItem) =>
            (curItem.connector === this.props.session?.connector || curItem.connector === 0) &&
            curItem.info !== "ConnectionWithCentralSystemRestored",
        )
        // ensure sorting by timestamp, which might not be the case if we got live data
        .sort((a, b) => a.timestamp - b.timestamp)
    );
  }

  private getConnectorName(conIndex: number, connectorName: string | undefined) {
    if (connectorName) {
      return connectorName;
    }
    // provide default name
    return `${this.context.formatMessage(messages.connector)} ${conIndex}`;
  }

  public render() {
    this.isOngoingSession = this.props.session?.stopTimestamp === undefined;
    const hasChargerControl =
      !this.props.ocppCharger?.connectionType ||
      this.props.ocppCharger?.connectionType === ChargerConnectionType.PROXY;

    return (
      <div id="chargingsession" className="flex-self flex-content">
        {!this.props.isRfidUser && (
          <div className="buttonbar d-print-none">
            <Button id="navigate-back" variant="secondary" onClick={() => this.props.navigate(-1)}>
              <FormattedMessage id="Back to Session List" />
            </Button>
            <Button
              className="ms-1"
              variant="secondary"
              title={this.context.formatMessage(messages.printHint)}
              onClick={() => {
                window.print();
              }}
            >
              <IconPrint />
            </Button>
          </div>
        )}

        <h1 title={this.props.session ? `Id: ${this.props.session.id}` : undefined}>
          {this.isOngoingSession ?
            <FormattedMessage id="Ongoing Charging Session" />
          : <>
              <FormattedMessage id="Charging Session" />{" "}
              <FormattedDate valueMs={(this.props.session?.startTimestamp ?? 0) * 1000} />
            </>
          }
        </h1>
        {isSet(this.props.ocppCharger?.fk_charger) &&
          this.isOngoingSession &&
          hasChargerControl && (
            <OcppCommandButtons
              chargerId={this.props.ocppCharger!.fk_charger}
              session={this.props.session}
            />
          )}
        {this.isOngoingSession &&
          this.props.isRfidUser &&
          (this.props.user?.accessRights?.isChargerPowerCtrlAllowed ?? false) &&
          this.props.ocppCharger?.hasSmartCharging === true && (
            <>
              <ChargingPowerControl transactionId={this.props.session?.id} />
              <h2>
                <FormattedMessage id="Charger" />
              </h2>
            </>
          )}
        {isSet(this.props.session) && isSet(this.props.ocppCharger) && this.renderSessionData()}
        <ChargingSessionGraph />
        {this.renderStatus()}
      </div>
    );
  }

  private renderSessionData() {
    return (
      <div id="sessiondata" className="row">
        <div className="col-12 d-md-none mb-3">
          {this.isOngoingSession ?
            <>
              <table className="datatable fullwidthwhite">
                <tbody>{this.renderSessionData1()}</tbody>
              </table>
              <h2>
                <FormattedMessage id="Charging Data" />
              </h2>
              <ChargerData isSessionView={true} />
            </>
          : <table className="datatable fullwidthwhite">
              <tbody>
                {this.renderSessionData1()}
                {this.renderSessionData2()}
              </tbody>
            </table>
          }
        </div>
        <div className="col-6 d-none d-md-block mb-3">
          <table className="datatable fullwidthwhite">
            <tbody>{this.renderSessionData1()}</tbody>
          </table>
        </div>
        <div className="col-6 d-none d-md-block mb-3">
          {this.isOngoingSession ?
            <ChargerData isSessionView={true} />
          : <table className="datatable fullwidthwhite">
              <tbody>{this.renderSessionData2()}</tbody>
            </table>
          }
        </div>
      </div>
    );
  }

  private renderSessionData1() {
    const session = this.props.session!;
    const charger = this.props.ocppCharger;
    const tariff = this.props.tariff;
    // a Keba slave charger delivers rfid_charger as the rfid code
    const rfid = session.rfid?.includes("_") ? session.rfid.split("_")[0] : session.rfid;
    const chargerName =
      session.rfid?.includes("_") ? session.rfid.split("_").pop()
      : isSet(charger) ? (charger.name ?? charger.chargepoint)
      : <FormattedMessage id="RESTRICTED CHARGER" />;

    return (
      <>
        <tr>
          <th>
            <FormattedMessage id="Charger" />
          </th>
          <td>{chargerName}</td>
        </tr>
        <tr>
          <th>
            <FormattedMessage id="Charger model" />
          </th>
          <td>
            {isSet(charger) ?
              `${charger.vendor} ${charger.model}`
            : <FormattedMessage id="RESTRICTED CHARGER" />}
          </td>
        </tr>
        {charger?.numberofconnectors === undefined ||
          (charger?.numberofconnectors > 1 && (
            <tr>
              <th>
                <FormattedMessage id="Connector" />
              </th>
              <td>{this.getConnectorName(session.connector, this.props.connector?.name)}</td>
            </tr>
          ))}
        {Boolean(this.props.user?.name) && (
          <tr>
            <th>
              <FormattedMessage id="User" />
            </th>
            <td>{this.props.user?.name}</td>
          </tr>
        )}
        {Boolean(rfid) && (
          <tr>
            <th>
              <FormattedMessage id="Charging card" />
            </th>
            <td>{rfid}</td>
          </tr>
        )}
        {Boolean(this.props.user?.carName) && (
          <tr>
            <th>
              <FormattedMessage id="Vehicle" />
            </th>
            <td>{this.props.user?.carName}</td>
          </tr>
        )}
        {isSet(tariff) && (
          <tr>
            <th>
              <FormattedMessage id="Tariff" />
            </th>
            <td>{tariff.name}</td>
          </tr>
        )}
        {this.isOngoingSession && (
          <tr>
            <th>
              <FormattedMessage id="Start" />
            </th>
            <td>
              <FormattedDate valueMs={session.startTimestamp * 1000} doZeroPadding={true} />{" "}
              <FormattedTime valueMs={session.startTimestamp * 1000} doZeroPadding={true} />
            </td>
          </tr>
        )}
        {isSet(session.costs) && (
          <tr>
            <th>
              <FormattedMessage id="Costs" />
            </th>
            <td className="nowrap">
              {`${roundFix(session.costs, 2)} ${tariff?.currency ?? "EUR"}`}
            </td>
          </tr>
        )}
        <tr>
          <th>
            <FormattedMessage id="Transaction ID" />
          </th>
          <td>{session.id}</td>
        </tr>
      </>
    );
  }

  private renderSessionData2() {
    const session = this.props.session!;
    const tariff = this.props.tariff;

    return (
      <>
        <tr>
          <th>
            <FormattedMessage id="Start" />
          </th>
          <td>
            <FormattedDate valueMs={session.startTimestamp * 1000} doZeroPadding={true} />{" "}
            <FormattedTime valueMs={session.startTimestamp * 1000} doZeroPadding={true} />
          </td>
        </tr>
        <tr>
          <th>
            <FormattedMessage id="End" />
          </th>
          <td>
            {this.isOngoingSession ?
              <FormattedMessage id="ongoing" />
            : <>
                <FormattedDate valueMs={session.stopTimestamp! * 1000} doZeroPadding={true} />{" "}
                <FormattedTime valueMs={session.stopTimestamp! * 1000} doZeroPadding={true} />
              </>
            }
          </td>
        </tr>
        <tr>
          <th>
            <FormattedMessage id="Duration" />
          </th>
          <td>
            {session.stopTimestamp &&
              formatTimestamp(session.stopTimestamp - session.startTimestamp)}
          </td>
        </tr>
        <tr>
          <th>
            <FormattedMessage id="Start meter value" />
          </th>
          <td>{roundFix(session.startMeter / 1000, 2)} kWh</td>
        </tr>
        <tr>
          <th>
            <FormattedMessage id="End meter value" />
          </th>
          <td>{session.stopMeter && `${roundFix(session.stopMeter / 1000, 2)} kWh`}</td>
        </tr>
        <tr>
          <th>
            <FormattedMessage id="Energy" />
          </th>
          <td className="nowrap">
            {session.stopMeter &&
              `${roundFix((session.stopMeter - session.startMeter) / 1000, 1)} kWh`}
          </td>
        </tr>
        {isSet(session.stopReason) && (
          <tr>
            <th>
              <FormattedMessage id="Stop reason" />
            </th>
            <td>
              {session.stopTimestamp && (
                <FormattedMessage id={`StopReason_${session.stopReason ?? ""}`} />
              )}
            </td>
          </tr>
        )}
      </>
    );
  }

  private renderStatus() {
    const statusRows = this.getStateEvents()?.map((curItem) => {
      return (
        <tr key={curItem.id}>
          <td>
            <FormattedDate valueMs={curItem.timestamp * 1000} doZeroPadding={true} />{" "}
            <FormattedTime
              valueMs={curItem.timestamp * 1000}
              doZeroPadding={true}
              showSeconds={true}
            />
          </td>
          <td className="optional-lg">
            {curItem.connector === 0 ?
              <FormattedMessage id="Station" />
            : <FormattedMessage id="Connector" />}
          </td>
          <td>
            <FormattedMessage id={`ChargerStatus_${curItem.statusCode ?? ""}`} />
          </td>
          <td>{curItem.errorCode !== "NoError" && curItem.errorCode}</td>
          <td>{curItem.info}</td>
        </tr>
      );
    });

    if (!statusRows || statusRows.length < 2) {
      //console.log("no status data", this.props.chargingStates);
      return <></>;
    }

    return (
      <div id="statustable" className="mt-3">
        <h1>
          <FormattedMessage id="Status Change Events" />
        </h1>
        <table className="datatable fullwidthwhite mb-2">
          <thead>
            <tr>
              <th>
                <FormattedMessage id="Timestamp" />
              </th>
              <th className="optional">
                <FormattedMessage id="Source" />
              </th>
              <th colSpan={2}>
                <FormattedMessage id="Event" />
              </th>
              <th>
                <FormattedMessage id="Details" />
              </th>
            </tr>
          </thead>
          <tbody>{statusRows}</tbody>
        </table>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ChargingSessionComponent));
